import {
    Component,
    EventEmitter,
    Input,
    Output,
    OnInit,
    ViewChild,
    forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DxSelectBoxComponent } from 'devextreme-angular';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { firstValueFrom } from 'rxjs';

import { BdsErpcOriginService, BdsSplcErpcService } from '@bds/data-access';
import { RtErpcOrigin } from '@bds/railtrac-models';
import {
    BdsErpcOriginGridDialogComponent,
    BdsErpcOriginGridDialogModel,
} from '@bds/smart-components';
import { BdsDialogInfoComponent, BdsDialogInfoModel } from '@bds/components';
import { BdsErpcOriginSelectDialogComponent } from 'projects/bds/smart-components/src/lib/components/bds-erpc-origin-select/bds-erpc-origin-select-dialog/bds-erpc-origin-select-dialog.component';

@Component({
    selector: 'bds-origin-select-box',
    templateUrl: './bds-origin-select-box.component.html',
    styleUrls: ['./bds-origin-select-box.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => BdsOriginSelectBoxComponent),
            multi: true,
        },
    ],
})
export class BdsOriginSelectBoxComponent implements OnInit, ControlValueAccessor {
    constructor(
        private dialog: MatDialog,
        private snackbar: MatSnackBar,
        public splcService: BdsSplcErpcService,
        private originCodeService: BdsErpcOriginService,
    ) {
        this.originCodeStore = originCodeService.getODataStore();
    }

    @Input() dxMatStyle!: string;
    @Input() dense!: boolean;
    @Input() matFloatLabel!: string;
    @Input() returnCity: string;
    @Input() returnState: string;
    @Output() selectedOriginCode = new EventEmitter<any>();

    @ViewChild(DxSelectBoxComponent) dxSelectBox;

    splc: string;
    originCodeSource: DataSource;
    originCodeStore: ODataStore;
    originCodeDisplayExpr: string[] = ['originCode', 'erpcOrigin', 'erpcState'];

    private _value: any;
    private onChange: (value: any) => void = () => {};
    private onTouched: () => void = () => {};

    get value(): any {
        return this._value;
    }

    set selectedValue(value: any) {
        this._value = value;
        this.onChange(value);
    }

    ngOnInit(): void {
        this.setUpOriginCodeOptions();
        this.getSplc();
    }

    writeValue(value: any): void {
        this._value = value;
    }

    registerOnChange(fn: (value: any) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    onValueChange(value: any): void {
        this.selectedValue = value;
        this.selectedOriginCode.emit(value);
    }

    originCodeDxSelectBoxDisplayExpr = (data: any): string => {
        if (!data) {
            return `(Missing Origin Code) - ${this.returnCity}, ${this.returnState}`;
        }
        return `${data.originCode} - ${data.erpcOrigin}, ${data.erpcState}`;
    };

    originCodeAddInlineButtonOptions = {
        type: 'default',
        icon: 'add',
        stylingMode: 'text',
        onClick: (): void => this.onAddOrigin(),
    };

    originCodeSearchInlineButtonOptions = {
        type: 'default',
        icon: 'search',
        stylingMode: 'text',
        onClick: (): void => this.searchForOriginCodeManager(),
    };

    async getSplc(): Promise<void> {
        const result = await firstValueFrom(
            this.originCodeService.getByCityState(this.returnCity, this.returnState),
        );
        if (result) {
            this.splc = result?.splc;
        }
    }

    onAddOrigin(): void {
        const origin: RtErpcOrigin = new RtErpcOrigin(this.returnCity, this.returnState, this.splc);
        const dialogData: BdsErpcOriginGridDialogModel = {
            isNew: true,
            origin: origin,
            originId: undefined,
            splcStore: this.splcService.getRtSplcErpcODataStore(),
            disabled: true,
        };

        const dialogRef = this.dialog.open(BdsErpcOriginGridDialogComponent, {
            width: '750px',
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.selectedValue = result[0].originCode;
            }
        });
    }

    searchForOriginCodeManager(): void {
        const dialogRef = this.dialog.open(BdsErpcOriginSelectDialogComponent, {
            data: { title: 'Select Origin' },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.selectedValue = result[0].originCode;
            }
        });
    }

    openSnackBar(message: string, action: string, success: boolean): void {
        const style = success ? 'success-snack' : 'error-snack';
        this.snackbar.open(message, action, {
            duration: 3000,
            panelClass: [style],
            horizontalPosition: 'right',
        });
    }

    setUpOriginCodeOptions(): void {
        this.originCodeSource = new DataSource({
            store: this.originCodeStore,
            paginate: true,
            pageSize: 100,
            searchExpr: this.originCodeDisplayExpr,
            select: ['originCode', 'erpcOrigin', 'erpcState', 'splc'],
            sort: ['erpcOrigin'],
            key: 'originCode',
        });
    }
}
